import { useEffect, useRef } from "react"

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/* Keyboard focus for Clara's two dialogs, Enlarge and the patient picker.

   Both opened over the chat but left focus behind them: Tab walked through the hidden
   page, a screen reader never learned a dialog had opened, and closing dropped focus to
   the top of the document. This moves focus into the dialog when it opens, keeps Tab and
   Shift+Tab inside it, and gives focus back to whatever opened it on close. */
export function useDialogFocus(open, containerRef, preferred) {
	const returnTo = useRef(null)

	useEffect(() => {
		if (!open) {
			return undefined
		}
		returnTo.current = document.activeElement
		const container = containerRef.current
		const focusables = () => (container ? Array.from(container.querySelectorAll(FOCUSABLE)) : [])
		// The search box when there is one: in the patient picker that is what a keyboard
		// user came to type in.
		const first = (preferred && container?.querySelector(preferred)) || focusables()[0]
		if (first) {
			first.focus()
		} else if (container) {
			container.focus()
		}

		const onKeyDown = event => {
			if (event.key !== "Tab" || !container) {
				return
			}
			const items = focusables()
			if (!items.length) {
				event.preventDefault()
				return
			}
			const firstItem = items[0]
			const lastItem = items[items.length - 1]
			if (event.shiftKey && document.activeElement === firstItem) {
				event.preventDefault()
				lastItem.focus()
			} else if (!event.shiftKey && document.activeElement === lastItem) {
				event.preventDefault()
				firstItem.focus()
			}
		}
		document.addEventListener("keydown", onKeyDown)
		return () => {
			document.removeEventListener("keydown", onKeyDown)
			const target = returnTo.current
			if (target && typeof target.focus === "function" && document.contains(target)) {
				target.focus()
			}
		}
	}, [open, containerRef, preferred])
}
